/**
 * RBAC-12 (Backend hotfix/p1-log-secrets, 8879b86b / af93519a):
 * - PUT /api/tasks/{id} → 403 si un employee envía assignee_id, employee_assignments o
 *   primary_assignee_id (aunque no cambien). Se quitan del payload en un solo lugar
 *   (interceptor de apiClient) cuando el usuario es employee.
 * - POST/PATCH/DELETE /api/task-assignments → solo owner/admin/manager.
 */
export const EMPLOYEE_FORBIDDEN_TASK_FIELDS = [
  'assignee_id',
  'employee_assignments',
  'primary_assignee_id',
  // alias de compatibilidad que el frontend manda junto con employee_assignments
  'assignees',
] as const;

const ASSIGNMENT_WRITERS = new Set(['admin', 'owner', 'manager']);

export function canManageTaskAssignments(role: string | null | undefined): boolean {
  return ASSIGNMENT_WRITERS.has(String(role || '').toLowerCase());
}

export function isEmployeeRole(role: string | null | undefined): boolean {
  return String(role || '').toLowerCase() === 'employee';
}

/** true para PUT /api/tasks/{id} (con o sin base URL / slash inicial / query); no subrutas. */
export function isTaskUpdateRequest(method: string | undefined, url: string | undefined): boolean {
  if (String(method || '').toLowerCase() !== 'put' || !url) return false;
  let path = url;
  try { path = new URL(url, 'http://x').pathname; } catch { /* usa url */ }
  return /^\/?api\/tasks\/[^/]+\/?$/.test(path.replace(/^\/+/, '/')) && !/^\/?api\/tasks\/(bulk|batch)/.test(path);
}

/** Copia del payload sin los campos de asignación si el rol es employee. */
export function stripEmployeeAssignmentFields<T>(data: T, role: string | null | undefined): T {
  if (!isEmployeeRole(role) || !data || typeof data !== 'object' || Array.isArray(data)) return data;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return data;
  const out: Record<string, unknown> = { ...(data as Record<string, unknown>) };
  for (const k of EMPLOYEE_FORBIDDEN_TASK_FIELDS) delete out[k];
  return out as T;
}

/** Rol guardado por AuthContext (localStorage 'auth_user'). */
export function getStoredUserRole(storage: Pick<Storage, 'getItem'> | undefined = typeof localStorage !== 'undefined' ? localStorage : undefined): string | null {
  try {
    const raw = storage?.getItem('auth_user');
    return raw ? (JSON.parse(raw)?.role ?? null) : null;
  } catch {
    return null;
  }
}

/** Para el interceptor: ajusta config.data de PUT /api/tasks/{id} cuando el usuario es employee. */
export function applyEmployeeTaskPayloadRule<C extends { method?: string; url?: string; data?: unknown }>(
  config: C,
  role: string | null | undefined = getStoredUserRole(),
): C {
  if (isTaskUpdateRequest(config.method, config.url)) {
    config.data = stripEmployeeAssignmentFields(config.data, role);
  }
  return config;
}
